<script setup lang="ts">
import type { PropType } from 'vue'
import { Line } from 'vue-chartjs'
import CrosshairPlugin from 'chartjs-plugin-crosshair'
import {
  Chart as ChartJS,
  Title,
  Tooltip,
  Legend,
  BarElement,
  CategoryScale,
  LinearScale,
  ArcElement,
  LineElement,
  PointElement,
  TimeScale,
} from 'chart.js'
import 'chartjs-adapter-date-fns'
import { enUS } from 'date-fns/locale'

ChartJS.register(
  Title,
  Tooltip,
  Legend,
  BarElement,
  CategoryScale,
  LinearScale,
  ArcElement,
  LineElement,
  PointElement,
  TimeScale,
)

const props = defineProps({
  data: {
    type: Object as PropType<any>,
    required: true,
  },
  turnOffAnimation: {
    type: Boolean,
    default: false,
  },
  yAxisTitle: {
    type: String,
    default: '',
  },
  showLinePoints: {
    type: Boolean,
    default: true,
  },
  mineral: {
    type: String,
    required: true,
  },
})

const { $theme, $rudderstack } = useNuxtApp()

const chartOptions: any = computed(() => {
  return {
    animation: !props.turnOffAnimation,
    hover: {
      intersect: false,
    },
    elements: {
      point: {
        radius: props.showLinePoints ? 3 : 0,
      },
    },
    responsive: true,
    // Uncaught (in promise) TypeError: Cannot read properties of undefined (reading 'enabled')
    // maintainAspectRatio: false,
    interaction: {
      intersect: false,
      mode: 'index',
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        titleFont: {
          family: 'Montserrat',
        },
        bodyFont: {
          family: 'Montserrat',
        },
        callbacks: {
          label(data: any) {
            const label = data.dataset.label
            return `${label}: ${data.raw}`
          },
        },
      },
      crosshair: {
        line: {
          color: $theme.colors.s['600'],
          width: 1,
        },
        zoom: {
          enabled: false,
        },
        snap: {
          enabled: true,
        },
        sync: {
          enabled: false,
        },
      },
    },
    onHover: () => {
      $rudderstack.trackChartInteraction({
        chart_title: `${props.mineral} Index Chart`,
        start_date: props.data.labels[0],
        end_date: props.data.labels[props.data.labels.length - 1],
        chart_type: 'line_chart',
      })
    },
    scales: {
      y: {
        ticks: {
          maxTicksLimit: 7,
          font: { family: 'Montserrat', size: '10px' },
        },
        title: {
          display: Boolean(props.yAxisTitle),
          text: props.yAxisTitle,
          font: { family: 'Montserrat', size: '12px' },
        },
      },
      x: {
        type: 'time',
        time: {
          unit: 'month',
          displayFormats: {
            datetime: 'MMM d, yyyy',
          },
        },
        bounds: 'ticks',
        adapters: {
          date: {
            locale: enUS,
          },
        },
        ticks: {
          autoSkip: true,
          autoSkipPadding: 10,
          font: { family: 'Montserrat', size: '10px' },
        },
      },
    },
  }
})

const chartRef = ref()
const onClickLegendItem = useOnClickLegendItem(chartRef)
</script>

<template>
  <MineralChartLegends
    class="mb-1"
    :datasets="data.datasets"
    @click-legend-item="onClickLegendItem"
  />
  <Line
    v-if="data"
    ref="chartRef"
    :data="data"
    :styles="{ minHeight: '0', height: '100%', minWidth: '0', width: '100%' }"
    :options="chartOptions"
    :plugins="[CrosshairPlugin]"
  />
</template>
